import { db } from '../config';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  doc,
  deleteDoc,
  Timestamp,
  DocumentData,
  writeBatch,
  limit
} from 'firebase/firestore';

export interface ContactNote {
  note: string;
  timestamp: Date;
  staffMember?: string;
}

export interface WaitingListEntry {
  id?: string;
  customerId: string;
  firstName: string;
  lastName: string;
  customerEmail: string;
  customerPhone: string;
  cellPhone?: string;
  isHomeowner: boolean;
  mailingAddress: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  localAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  vesselName: string;
  vesselYear: string;
  vesselMake: string;
  vesselModel: string;
  vesselColor: string;
  vesselLength: number;
  vesselRegistrationNumber: string;
  vesselRegistrationState: string;
  vesselRegistrationYear: string;
  status: 'pending' | 'approved' | 'rejected';
  priority: number;
  dateAdded: Date;
  notes?: string;
  contactNotes?: ContactNote[];
}

// Add a new entry to the waiting list
export const addToWaitingList = async (entry: Omit<WaitingListEntry, 'id' | 'dateAdded'>): Promise<void> => {
  try {
    // Get the current highest order
    const waitingListRef = collection(db, 'waitingList');
    const q = query(waitingListRef, orderBy('order', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);
    const highestOrder = querySnapshot.docs[0]?.data().order ?? -1;

    const docRef = await addDoc(waitingListRef, {
      ...entry,
      dateAdded: Timestamp.now(),
      order: highestOrder + 1
    });
  } catch (error) {
    console.error('Error adding to waiting list:', error);
    throw error;
  }
};

// Get all waiting list entries
export const getWaitingList = async (): Promise<WaitingListEntry[]> => {
  try {
    const waitingListRef = collection(db, 'waitingList');
    // First try to get all documents without ordering
    const querySnapshot = await getDocs(waitingListRef);
    console.log('Raw Firestore data:', querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        customerId: data.customerId,
        firstName: data.firstName,
        lastName: data.lastName,
        customerEmail: data.customerEmail,
        customerPhone: data.customerPhone,
        cellPhone: data.cellPhone,
        isHomeowner: data.isHomeowner,
        mailingAddress: data.mailingAddress,
        localAddress: data.localAddress,
        vesselName: data.vesselName,
        vesselYear: data.vesselYear,
        vesselMake: data.vesselMake,
        vesselModel: data.vesselModel,
        vesselColor: data.vesselColor,
        vesselLength: data.vesselLength,
        vesselRegistrationNumber: data.vesselRegistrationNumber,
        vesselRegistrationState: data.vesselRegistrationState,
        vesselRegistrationYear: data.vesselRegistrationYear,
        status: data.status || 'pending',
        priority: data.priority || 0,
        dateAdded: data.dateAdded?.toDate() || new Date(),
        notes: data.notes,
        contactNotes: data.contactNotes?.map((note: any) => ({
          ...note,
          timestamp: note.timestamp?.toDate() || new Date()
        }))
      };
    });
  } catch (error) {
    console.error('Error getting waiting list:', error);
    throw error;
  }
};

// Update a waiting list entry
export const updateWaitingListEntry = async (id: string, data: Partial<WaitingListEntry>): Promise<void> => {
  try {
    const entryRef = doc(db, 'waitingList', id);
    await updateDoc(entryRef, data);
  } catch (error) {
    console.error('Error updating waiting list entry:', error);
    throw new Error('Failed to update waiting list entry');
  }
};

// Add a contact note to an entry
export const addContactNote = async (entryId: string, note: string): Promise<void> => {
  try {
    const entryRef = doc(db, 'waitingList', entryId);
    const entry = await getDocs(query(collection(db, 'waitingList'), where('id', '==', entryId)));
    
    if (entry.empty) {
      throw new Error('Waiting list entry not found');
    }
    
    const currentNotes = entry.docs[0].data().contactNotes || [];
    await updateDoc(entryRef, {
      contactNotes: [...currentNotes, {
        note,
        timestamp: Timestamp.now()
      }],
      lastContacted: Timestamp.now()
    });
  } catch (error) {
    console.error('Error adding contact note:', error);
    throw new Error('Failed to add contact note');
  }
};

// Remove an entry from the waiting list
export const removeFromWaitingList = async (id: string): Promise<void> => {
  try {
    const entryRef = doc(db, 'waitingList', id);
    await deleteDoc(entryRef);
  } catch (error) {
    console.error('Error removing from waiting list:', error);
    throw new Error('Failed to remove from waiting list');
  }
};

// Update priority of an entry
export const updatePriority = async (id: string, priority: number): Promise<void> => {
  try {
    const entryRef = doc(db, 'waitingList', id);
    await updateDoc(entryRef, { priority });
  } catch (error) {
    console.error('Error updating priority:', error);
    throw new Error('Failed to update priority');
  }
};

// Get entries by status
export const getEntriesByStatus = async (status: WaitingListEntry['status']) => {
  try {
    const waitingListRef = collection(db, 'waitingList');
    const q = query(
      waitingListRef,
      where('status', '==', status),
      orderBy('priority', 'desc'),
      orderBy('dateAdded', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as WaitingListEntry[];
  } catch (error) {
    console.error('Error getting entries by status:', error);
    throw error;
  }
};

export const reorderWaitingList = async (entryIds: string[]) => {
  const batch = writeBatch(db);
  
  // Update each entry's order based on its position in the array
  entryIds.forEach((id, index) => {
    const entryRef = doc(db, 'waitingList', id);
    batch.update(entryRef, { order: index });
  });

  await batch.commit();
}; 